import { useState, useEffect, useMemo, useRef } from 'react'; // useEffect: page data only
import {
  collection, query, where, onSnapshot, doc, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { MoneyCalendar, ViewToggle } from '../components/MoneyCalendar';
import { Paging, DeleteIconButton } from '../components/ui';
import { Button, Modal, ConfirmModal, FormField } from '../components/ui';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  INCOME_CATEGORIES, buildEntry, postEntry, entryId, linesIncome,
} from '../utils/ledger';
import type { BankAccount, BankTransaction } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Add / Edit modal ─────────────────────────────────────────────────────

function IncomeModal({ editing, accounts, onClose }: {
  editing: BankTransaction | null; accounts: BankAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const isEdit = !!editing;
  // Fresh mount per open → init from props, no effect.
  const [accountId, setAccountId]     = useState(editing ? editing.accountId : ((accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? ''));
  const [category, setCategory]       = useState(editing?.category ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [amount, setAmount]           = useState(editing ? String(editing.amount ?? '') : '');
  const [date, setDate]               = useState(editing?.date ?? todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const acct = accounts.find(a => a.id === accountId) ?? null;

  async function handleSave() {
    const amt = parseFloat(amount);
    if (!accountId) return setError('Choose the account it went into.');
    if (!category) return setError('Choose a category.');
    if (!description.trim()) return setError('Enter a short description.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (!date) return setError('Pick the date.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !acct) { setError('Not connected.'); setSaving(false); return; }

    try {
      if (isEdit) {
        const txRef  = doc(db, 'bankTransactions', editing.id);
        const accRef = doc(db, 'bankAccounts', editing.accountId);
        await runTransaction(db, async tx => {
          const accSnap = await tx.get(accRef);
          if (!accSnap.exists()) throw new Error('That bank account no longer exists.');
          const delta = amt - (editing.amount ?? 0);   // income → balance moves with the amount
          tx.update(txRef, { amount: amt, category, description: description.trim(), date });
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + delta });
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'income', sourceId: editing.id,
            sourceCollection: 'bankTransactions', date, currency: editing.currency,
            createdBy: user?.id ?? editing.createdBy, note: description.trim(),
            lines: linesIncome(amt, editing.accountId),
          }), entryId('income', editing.id));
        });
      } else {
        const txRef  = doc(collection(db, 'bankTransactions'));
        const accRef = doc(db, 'bankAccounts', acct.id);
        await runTransaction(db, async tx => {
          const accSnap = await tx.get(accRef);
          if (!accSnap.exists()) throw new Error('That bank account no longer exists.');
          tx.set(txRef, stripUndef({
            businessId: business.id, accountId: acct.id, accountName: acct.name,
            currency: acct.currency, direction: 'in', amount: amt, date,
            description: description.trim(), referenceType: 'income', category,
            createdAt: new Date().toISOString(), createdBy: user!.id,
          }));
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + amt });
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'income', sourceId: txRef.id,
            sourceCollection: 'bankTransactions', date, currency: acct.currency,
            createdBy: user!.id, note: description.trim(),
            lines: linesIncome(amt, acct.id),
          }), entryId('income', txRef.id));
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Edit Income' : 'Record Income'}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} loading={saving}>{isEdit ? 'Save' : 'Record'}</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="form-row-2">
        <FormField label="Into account" required>
          <select value={accountId} onChange={e => setAccountId(e.target.value)} disabled={isEdit}>
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <div className="form-row-2">
        <FormField label="Category" required>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">Select…</option>
            {INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label={`Amount (${acct?.currency ?? 'USD'})`} required>
          <input type="number" step="0.01" min="0.01" value={amount}
            onChange={e => setAmount(e.target.value)} className="no-spinner" />
        </FormField>
      </div>
      <FormField label="Description" required>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What was this for?" />
      </FormField>
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function IncomePage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [txns, setTxns] = useState<BankTransaction[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  // List or Calendar (like Receivable / Payable); the calendar has its own month arrows.
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calYM, setCalYM] = useState(() => ({ y: new Date().getFullYear(), m: new Date().getMonth() }));
  const stepMonth = (d: number) => setCalYM(({ y, m }) => { const t = new Date(y, m + d, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modal, setModal] = useState<'add' | { edit: BankTransaction } | null>(null);
  const [confirmDel, setConfirmDel] = useState<BankTransaction | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(
      query(collection(db, 'bankTransactions'), where('referenceType', '==', 'income')),
      snap => { setTxns(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankTransaction))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    return () => { u1(); u2(); };
  }, [business]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const baseRows = useMemo(() => {
    if (!window) return [];
    const q = searchQ.trim().toLowerCase();
    return txns
      .filter(t => viewMode === 'calendar' || inWindow(t.date, window))
      .filter(t => !q || [t.description, t.category, t.referenceNo].some(v => (v ?? '').toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [txns, window, searchQ, viewMode]);
  // Excel-style filters on Category and Account.
  const filterColumns = useMemo<FilterColumn<BankTransaction>[]>(() => [
    { key: 'category', label: 'Category', get: t => t.category },
    { key: 'account',  label: 'Account',  get: t => t.accountName },
  ], []);
  const { rows, filters: colFilters } = useColumnFilters(baseRows, filterColumns);
  // 50 rows at a time; a new filter goes back to page 1.
  const pg = usePaging(rows, [JSON.stringify(window), searchQ, colFilters.stamp].join('|'));
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [rows.length > 0, loading]);
  // Figures: the date range in List, the month shown in Calendar.
  const figRows = useMemo(() => viewMode === 'calendar'
    ? rows.filter(t => t.date.startsWith(`${calYM.y}-${String(calYM.m + 1).padStart(2, '0')}`))
    : rows, [rows, viewMode, calYM]);
  const total = useMemo(() => figRows.reduce((s, t) => s + (t.amount ?? 0), 0), [figRows]);

  async function doDelete(t: BankTransaction) {
    setBusy(true);
    const db = getClientDb();
    if (!db) { setBusy(false); return; }
    try {
      const accRef = doc(db, 'bankAccounts', t.accountId);
      await runTransaction(db, async tx => {
        const accSnap = await tx.get(accRef);
        tx.delete(doc(db, 'bankTransactions', t.id));
        if (accSnap.exists()) tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - (t.amount ?? 0) });
        tx.delete(doc(db, 'journalEntries', entryId('income', t.id)));
      });
    } finally { setBusy(false); setConfirmDel(null); }
  }

  return (
    <AppLayout
      title="Income"
      subtitle="Money in that isn't a customer sale — interest, refunds, other income."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Description, category, ref…" label="Search income" />
        {viewMode === 'list' && (
          <DateRangeButton
            value={dateKey} onChange={setDateKey}
            customFrom={customFrom} customTo={customTo}
            onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
          />
        )}
        {(searchQ || dateKey !== 'this-month' || colFilters.active.length > 0) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); colFilters.clear(); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <ViewToggle value={viewMode} onChange={setViewMode} />
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setModal('add')}
          style={{ height: 34, boxSizing: 'border-box' }}>Record Income</Button>
      </>}
    >
      <StatChips items={[
        { label: 'Income', value: money(total), tone: 'primary' },
        { label: 'Entries', value: String(figRows.length) },
      ]} end={<ColumnFilterChips filters={colFilters} inline />} />

      {viewMode === 'calendar' ? (
        loading
          ? <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
          : <MoneyCalendar
              items={rows.map(t => ({
                id: t.id, date: t.date, label: t.description || t.category || 'Income',
                amount: t.amount ?? 0, currency: t.currency, tone: 'in' as const,
                title: `${t.description || t.category || 'Income'} · ${t.accountName} · ${fmtMoney(t.amount ?? 0, t.currency)}`,
              }))}
              year={calYM.y} month={calYM.m} onPrev={() => stepMonth(-1)} onNext={() => stepMonth(1)}
              onSelect={id => { const t = rows.find(x => x.id === id); if (t) setModal({ edit: t }); }}
              tones={['in']} summary="received"
            />
      ) : loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>{baseRows.length ? 'Nothing matches these filters' : 'No income here'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{baseRows.length ? 'Try clearing a filter.' : 'Record one with the button above.'}</div>
        </div>
      ) : (
        <>
          <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 170 }} className="th-filterable">Category<ColumnFilterButton filters={colFilters} colKey="category" label="Category" /></th>
                  <th>Description</th>
                  <th style={{ width: 160 }} className="th-filterable">Account<ColumnFilterButton filters={colFilters} colKey="account" label="Account" /></th>
                  <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pg.pageRows.map(t => (
                  <tr key={t.id} onClick={() => setModal({ edit: t })} style={{ cursor: 'pointer' }}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(t.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{t.category || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{t.description}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{t.accountName}</td>
                    <td style={{ fontSize: 12, color: 'var(--success)', fontFamily: 'var(--font-num)', textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney(t.amount ?? 0, t.currency)}
                    </td>
                    <td className="col-actions" onClick={ev => ev.stopPropagation()}>
                      <DeleteIconButton onClick={() => setConfirmDel(t)} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>Total · {rows.length} {rows.length === 1 ? 'entry' : 'entries'}</td>
                  <td style={{ fontSize: 12, fontWeight: 700, color: 'var(--success)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{money(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <Paging page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} noun="entry" />
        </>
      )}

      {modal === 'add' && <IncomeModal editing={null} accounts={accounts} onClose={() => setModal(null)} />}
      {modal && typeof modal === 'object' && 'edit' in modal && (
        <IncomeModal editing={modal.edit} accounts={accounts} onClose={() => setModal(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this income entry?"
          message={`"${confirmDel.description}" (${fmtMoney(confirmDel.amount ?? 0, confirmDel.currency)}) will be removed and ${confirmDel.accountName}'s balance reduced.`}
          confirmLabel="Delete" loading={busy}
          onConfirm={() => doDelete(confirmDel)} onClose={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
